import { TimeSeriesChartPageSection } from 'components/chart/TimeSeriesChartPageSection';
import { LineChartRenderer } from 'components/chart/renderer/LineChartRenderer';
import { MeasureCardRow } from 'components/MeasureCardRow';
import { MARKDOWN_VS_CATEGORY_CODE, toMarkdownVsCategoryMeasure } from 'components/price/priceMeasures';
import { MeasureUnitConfig } from 'components/measure-unit/MeasureUnitConfig';
import { useDatePickerLabels } from 'hooks/useDatePickerLabels';
import { useDataRequest } from 'hooks/useDataRequest';
import { usePriceEntityAttribute, usePriceKpiData, usePriceTimeSeries } from 'pages/entities/queryHooks';
import { Filters } from 'services/data.service';
import { Attribute, AttributeCode, Measure, useModelService } from 'services/model/model.service';
import { createTimeSeriesChartConfig } from 'utils/createTimeSeriesChartConfig';
import { useIntl } from 'react-intl';
import { useEffect, useState } from 'react';
import { PriceBreakdownTable } from 'pages/entities/components/PriceBreakdownTable';
import { useInfoDrawer } from 'context/InfoDrawerContext';
import { PriceEntityInfoContent } from 'pages/entities/components/PriceEntityInfoContent';

const PRICE_TABLE_ATTRIBUTES: AttributeCode[] = [
  'chnMarketplace',
  'chnCountry',
  'styBrandColor',
  'artEan',
  'prdNumber',
];

const PRICE_MEASURE_FORMAT_CONFIGS: Partial<Record<string, MeasureUnitConfig>> = {
  [MARKDOWN_VS_CATEGORY_CODE]: { isDiff: true },
};

interface PriceTabProps {
  filterDecorator: (filter: Filters) => Filters;
  availableTables: Attribute[];
}

export function PriceTab({ filterDecorator, availableTables }: PriceTabProps) {
  const intl = useIntl();
  const labelContext = useDatePickerLabels();
  const { setContent } = useInfoDrawer();
  const { getAttribute, getMeasures } = useModelService();
  const [avgSoldPrice, avgPriceMarkdown, soldAtOriginalPrice] = getMeasures([
    'itemSlsGmvValExtOD',
    'itemSlsMkdRateExtOD',
    'slsAtOmvQtyRateOD',
  ]);
  const requestData = useDataRequest();
  const adjustedRequestData = { ...requestData, filters: filterDecorator(requestData.filters) };
  const isCategoryEntity = usePriceEntityAttribute() === 'prdCategory';
  const markdownVsReferenceLabelId = isCategoryEntity ? 'price.kpi.markdownVsAll' : 'price.kpi.markdownVsCategory';
  const markdownVsCategoryMeasure = toMarkdownVsCategoryMeasure(avgPriceMarkdown, markdownVsReferenceLabelId);
  const measures = [avgSoldPrice, avgPriceMarkdown, soldAtOriginalPrice, markdownVsCategoryMeasure];
  const priceTables = PRICE_TABLE_ATTRIBUTES.flatMap(attributeCode => {
    const attribute = availableTables.find(({ code }) => code === attributeCode);
    return attribute ? [attribute] : [];
  });
  const [selectedMeasureCode, setSelectedMeasureCode] = useState(measures[0].code);
  const timeSeriesMeasure = measures.find(measure => measure.code === selectedMeasureCode) ?? measures[0];

  const { data: totalsData } = usePriceKpiData(adjustedRequestData);
  const { data: timeSeriesData } = usePriceTimeSeries(adjustedRequestData);
  const timeSeriesChartMeasure = timeSeriesMeasure.labelId
    ? {
        ...timeSeriesMeasure,
        metric: {
          ...timeSeriesMeasure.metric,
          name: intl.formatMessage({ id: timeSeriesMeasure.labelId }),
        },
      }
    : timeSeriesMeasure;

  useEffect(() => {
    setContent({
      titleKey: 'entities.lens.price',
      content: <PriceEntityInfoContent />,
    });
    return () => setContent(null);
  }, [setContent]);

  let timeSeriesRenderer = null;
  if (timeSeriesData) {
    const chartConfig = createTimeSeriesChartConfig({
      getAttribute,
      measures: [timeSeriesChartMeasure],
      request: adjustedRequestData,
      intl,
      response: timeSeriesData,
      labelContext,
    });
    if (chartConfig.value) {
      chartConfig.value.formatConfig = PRICE_MEASURE_FORMAT_CONFIGS[timeSeriesMeasure.code];
    }
    timeSeriesRenderer = new LineChartRenderer(chartConfig);
  }

  const onMeasureSelect = (measure: Measure) => () => {
    if (measure.code !== timeSeriesMeasure.code) {
      setSelectedMeasureCode(measure.code);
    }
  };

  return (
    <>
      {totalsData && (
        <MeasureCardRow
          measures={measures}
          totalsData={totalsData}
          selectedMeasureCode={timeSeriesMeasure.code}
          onMeasureSelect={onMeasureSelect}
          testid="price-measure-card-row"
          measureFormatConfigs={PRICE_MEASURE_FORMAT_CONFIGS}
        />
      )}
      <TimeSeriesChartPageSection
        timeSeriesMeasure={timeSeriesChartMeasure}
        timeSeriesRenderer={timeSeriesRenderer}
        showGroupByToggle={false}
        dataTestId="price"
      />
      <PriceBreakdownTable
        filterDecorator={filterDecorator}
        availableTables={priceTables}
        isCategoryEntity={isCategoryEntity}
      />
    </>
  );
}
